import { Link } from "react-router-dom";
import { MapPin, Loader2, ImageOff, Sparkles, Trash2, Check } from "lucide-react";
import { QualityBadge } from "@/components/AIAnalysisCard";
import { fileUrl } from "@/lib/api";
import { priceRange, statusLabel, STATUS_STYLES, timeAgo } from "@/lib/constants";

export const StatusPill = ({ status, testId = "lead-status-badge" }) => (
  <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`} data-testid={testId}>{statusLabel(status)}</span>
);

const SelectBox = ({ checked, onChange, id }) => (
  <button type="button" role="checkbox" aria-checked={checked} aria-label="Select lead" onClick={onChange} data-testid={`lead-select-${id}`}
    className={`absolute left-4 top-4 z-10 flex h-6 w-6 items-center justify-center rounded-md border-2 shadow-sm transition-colors ${
      checked ? "border-[color:var(--brand)] bg-[color:var(--brand)] text-white" : "border-white bg-white/90 text-transparent hover:border-slate-300"}`}>
    <Check className="h-4 w-4" strokeWidth={3} />
  </button>
);

export const LeadCard = ({ lead, selected = false, onToggle, onDelete }) => (
  <div className="relative">
    {onToggle && <SelectBox checked={selected} onChange={() => onToggle(lead.id)} id={lead.id} />}
    {onDelete && (
      <button type="button" onClick={() => onDelete(lead.id)} aria-label="Delete lead" data-testid={`lead-delete-${lead.id}`}
        className="absolute bottom-3 right-3 z-10 flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/15">
        <Trash2 className="h-4 w-4" />
      </button>
    )}
    <LeadCardBody lead={lead} selected={selected} />
  </div>
);

const LeadCardBody = ({ lead, selected }) => {
  const a = lead.ai_analysis;
  const photo = lead.photos[0];
  return (
    <Link to={`/leads/${lead.id}`} data-testid={`lead-card-${lead.id}`}
      className={`group flex gap-4 rounded-2xl border bg-card p-3 shadow-soft transition-[box-shadow,transform,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-lift active:scale-[0.99] ${selected ? "border-[color:var(--brand)] ring-1 ring-[color:var(--brand)]" : ""}`}>
      <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-muted sm:h-28 sm:w-28">
        {photo ? <img src={fileUrl(photo.path)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" data-testid="lead-card-thumbnail" />
          : <div className="flex h-full items-center justify-center text-muted-foreground"><ImageOff className="h-5 w-5" /></div>}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-between py-0.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-foreground" data-testid="lead-card-name">{lead.contact.name}</p>
            <p className="mt-0.5 truncate text-sm text-muted-foreground" data-testid="lead-card-service">{lead.service_type}</p>
            <p className="flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate" data-testid="lead-card-city">{lead.contact.city}</span></p>
          </div>
          {a && <QualityBadge quality={a.lead_quality} />}
        </div>
        <div className="mt-2">
          {a ? <p className="text-xl font-bold tracking-tight text-foreground" data-testid="lead-card-price">{priceRange(a)}</p>
            : lead.ai_status === "pending" ? <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="lead-card-price"><Loader2 className="h-4 w-4 animate-spin" />Analyzing...</p>
            : <p className="text-sm text-muted-foreground" data-testid="lead-card-price">No estimate</p>}
          <div className="mt-1.5 flex items-center gap-2">
            <StatusPill status={lead.status} />
            {lead.preview_status === "done" && (
              <span className="inline-flex items-center gap-1 rounded-md bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-500/15 dark:text-violet-300" data-testid="lead-card-preview-badge">
                <Sparkles className="h-3 w-3" />Preview
              </span>
            )}
            <span className="text-xs text-muted-foreground" data-testid="lead-card-date">{timeAgo(lead.created_at)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};
